import { useEffect, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { useAuthContext } from '../../../core/providers/AuthProvider'
import { apiClient, setTokens } from '../../../core/http'
import { authService } from '../../../core/services'
import { toast } from 'react-toastify'
import { LanguageSwitcher } from 'components/LanguageSwitcher'
import { openWindow } from 'models/window/actions'
import { modalNames } from 'features/modal/constants/names'
import c from './assets/sass/login.module.scss'

let _autoLoginAttempted = false
let _autoLoginTimeout = null

export const Login = () => {
    const { t } = useTranslation()
    const dispatch = useDispatch()
    const { loginWithTelegram, isLoading, error, refreshUser } =
        useAuthContext()
    const [siteId, setSiteId] = useState('')
    const [isProcessing, setIsProcessing] = useState(false)
    const [needsSiteId, setNeedsSiteId] = useState(false)

    const handleAuth = useCallback(
        async (forceSiteId = null) => {
            setIsProcessing(true)

            try {
                let initData = null
                if (window.Telegram?.WebApp) {
                    const tg = window.Telegram.WebApp
                    initData = tg.initData
                }

                const currentSiteId = forceSiteId || siteId?.trim() || null

                if (initData && initData.length > 0) {
                    try {
                        const result = await loginWithTelegram(
                            initData,
                            currentSiteId
                        )
                        if (result && result.success) {
                            await new Promise((resolve) =>
                                setTimeout(resolve, 200)
                            )
                            await refreshUser()
                        }
                        return
                    } catch (authError) {
                        const errorCode = authError?.response?.data?.code
                        const errorMsg = authError?.response?.data?.error

                        if (errorCode === 'SITE_ID_REQUIRED') {
                            setNeedsSiteId(true)
                            setIsProcessing(false)
                            return
                        }

                        if (errorCode === 'SITE_ID_TAKEN') {
                            toast(t('login.error.siteIdTaken'), {
                                type: 'error',
                            })
                            localStorage.removeItem('userSiteId')
                            setSiteId('')
                            setNeedsSiteId(true)
                            setIsProcessing(false)
                            return
                        }

                        if (errorCode === 'USER_BANNED') {
                            toast(t('login.error.userBanned'), {
                                type: 'error',
                            })
                            setIsProcessing(false)
                            return
                        }

                        if (errorCode === 'CHANNEL_SUBSCRIPTION_REQUIRED') {
                            const channelLink =
                                authError?.response?.data?.channelLink ||
                                'https://t.me/ggstandof'
                            toast.error(t('login.error.channelRequired'), {
                                autoClose: 5000,
                            })
                            if (window.Telegram?.WebApp) {
                                window.Telegram.WebApp.openTelegramLink(
                                    channelLink
                                )
                            } else {
                                window.open(channelLink, '_blank')
                            }
                            setIsProcessing(false)
                            return
                        }

                        if (errorMsg) {
                            toast(errorMsg, { type: 'error' })
                            setIsProcessing(false)
                            return
                        }

                        throw authError
                    }
                }
                if (process.env.NODE_ENV === 'development' || process.env.REACT_APP_ENV === 'development') {
                    try {
                        const result = await loginWithTelegram('', currentSiteId)
                        if (result && result.success) {
                            await new Promise((resolve) => setTimeout(resolve, 200))
                            await refreshUser()
                        }
                        return
                    } catch (devErr) {
                        console.error('Dev auth failed:', devErr)
                    }
                }
                authService.diag('auth_attempt', {
                    hasInitData: !!initData,
                    initDataLen: (initData || '').length,
                    siteId: currentSiteId,
                    baseURL: window.location.origin,
                })
                toast(t('login.error.noInitData'), { type: 'error', toastId: 'no-init-data', autoClose: 6000 })
                return
            } catch (error) {
                console.error('Ошибка авторизации:', error)
                const errorCode = error?.response?.data?.code

                if (errorCode === 'INVALID_TELEGRAM_DATA') {
                    toast(t('login.error.telegramData'), { type: 'error' })
                } else {
                    toast(t('login.error.auth'), { type: 'error' })
                }

                authService.diag('auth_error', {
                    message: error?.message || String(error),
                    status: error?.response?.status,
                    errorCode: error?.response?.data?.code,
                    errorMessage: error?.response?.data?.error,
                })
            } finally {
                setIsProcessing(false)
            }
        },
        [siteId, loginWithTelegram, refreshUser, t]
    )

    useEffect(() => {
        if (_autoLoginAttempted) return
        _autoLoginAttempted = true

        const savedSiteId = localStorage.getItem('userSiteId')
        if (savedSiteId) {
            setSiteId(savedSiteId)
        }

        if (window.Telegram?.WebApp) {
            const tg = window.Telegram.WebApp
            tg.ready()
            tg.expand()
            authService.diag('webapp_ready', {
                hasInitData: !!tg.initData,
                initDataLen: (tg.initData || '').length,
                platform: tg.platform,
                savedSiteId: !!savedSiteId,
            })
            

            if (tg.initData && tg.initData.length > 0) {
                if (_autoLoginTimeout) clearTimeout(_autoLoginTimeout)
                _autoLoginTimeout = setTimeout(() => {
                    handleAuth(savedSiteId || null)
                }, 300)
            }
        } else {
            authService.diag('webapp_unavailable', {
                userAgent: navigator.userAgent,
                origin: window.location.origin,
            })
        }

        return () => {
            if (_autoLoginTimeout) {
                clearTimeout(_autoLoginTimeout)
                _autoLoginTimeout = null
            }
        }
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && siteId.trim() && !isProcessing && !isLoading) {
            e.preventDefault()
            handleAuth()
        }
    }

    const formDisabled = isProcessing || isLoading

    return (
        <>
            <div className={c.languageSwitcherWrapper}>
                <LanguageSwitcher />
            </div>
            <div className={c.component}>
                <h3 className={c.title}>{t('login.title')}</h3>
                <span className={c.subTitle}>
                    {formDisabled && !needsSiteId
                        ? t('login.authorizing')
                        : t('login.subtitle')}
                </span>

                {error && !needsSiteId && <div className={c.error}>{error}</div>}

                <div className={c.form}>
                    <input
                        type="text"
                        className={c.field}
                        placeholder={t('login.placeholder')}
                        value={siteId}
                        onChange={(e) => setSiteId(e.target.value)}
                        onKeyDown={handleKeyDown}
                        disabled={isProcessing}
                        autoFocus
                    />
                    <button
                        className={c.helpButton}
                        onClick={() => dispatch(openWindow(modalNames.SITE_ID_HELP_MODAL))}
                        type="button"
                    >
                        {t('login.howToFindSiteId')}
                    </button>
                    <button
                        className={c.button}
                        onClick={() => handleAuth()}
                        disabled={isProcessing || !siteId.trim()}
                        style={{
                            opacity:
                                !siteId.trim() || isProcessing
                                    ? 0.5
                                    : 1,
                            cursor:
                                !siteId.trim() || isProcessing
                                    ? 'not-allowed'
                                    : 'pointer',
                        }}
                    >
                        {isProcessing
                            ? t('login.loading')
                            : t('login.confirm')}
                    </button>
                </div>
            </div>
        </>
    )
}
